import { inject, Injectable } from '@angular/core';
import { PaymentRequest } from '../models/booking.model';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { APP_CONFIG, SKIP_LOADING } from '@ribera-web/app-token';
import { BehaviorSubject, map, Observable, of } from 'rxjs';
import { PaymentRejection, PaymentSubMethod } from '../models/payment.model';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private http = inject(HttpClient);
  private config = inject(APP_CONFIG);
  private readonly API_URL =
    this.config.PAYMENT_MICROSERVICE_API+ '/api/v1/payments';
    //this.config.BOOKING_MICROSERVICE_API

  private apiUrl = `${this.config.PAYMENT_MICROSERVICE_API.replace(
    /\/+$/,
    ''
  )}/api/v1`;

  private tokenDataSubject = new BehaviorSubject<any>(null);

  /**
   * Obtener lista de monedas disponibles
   */
  getCurrencies(): Observable<string[]> {
    return this.http
      .get<{
        result: boolean;
        data: Array<{
          currencyId: number;
          code: string;
          name: string;
          symbol: string;
          isActive: boolean;
        }>;
        timestamp: string;
        status: number;
      }>(`${this.apiUrl}/currencies`)
      .pipe(
        map((response) => {
          if (response?.data && Array.isArray(response.data)) {
            return response.data.map((currency) => currency.name);
          }
          return [];
        })
      );
  }

  /**
   * Obtener lista de métodos de pago (tipos de operación)
   */
  getPaymentMethods(): Observable<PaymentSubMethod[]> {
    return this.http
      .get<{data: PaymentSubMethod[]}>(`${this.apiUrl}/payment-sub-methods/payment-method/1`, {
        context: new HttpContext().set(SKIP_LOADING, true),
      })
      .pipe(
        map((response) => {
          return response.data;
        })
      );
  }

  getAllPaymentMethods(): Observable<any[]> {
    return this.http
      .get<{data: any[]}>(`${this.apiUrl}/payment-methods`)
      .pipe(
        map((response) => {
          return response.data;
        })
      );
  }

  getOtherPaymentMethods(): Observable<any[]> {
    return this.http
      .get<{data: any[]}>(`${this.apiUrl}/payment-methods/other`)
      .pipe(
        map((response) => {
          return response.data;
        })
      );
  }

  getExchangeRate(): Observable<{ buys: number; sale: number }> {
    // return this.http.get<{ buys: number; sale: number }>(`${this.apiUrl}/exchange-rate/current`);
    return of({ buys: 3.50, sale: 3.50 });
  }

  /**
 * Obtener motivo de rechazo
 */
  getPaymentRejection(paymentId: number): Observable<PaymentRejection> {
    return this.http
      .get<{data: PaymentRejection}>(`${this.apiUrl}/payment-rejection/payment/${paymentId}`, {
        context: new HttpContext().set(SKIP_LOADING, true),
      })
      .pipe(
        map((response) => {
          return response.data;
        })
      );
  }

  savePayment( request: PaymentRequest, files: File[] ): Observable<any> {

    const url = `${this.API_URL}/v2`;
    
    const formData = new FormData();
    // agregar JSON como Blob
    formData.append(
      "data",
      new Blob([JSON.stringify(request)], { type: "application/json" })
    );

    // agregar cada imagen
    files.forEach(file => formData.append("file", file));

    return this.http.post(url, formData);
  }
  
  updatePaymentStatus(paymentId: number, status: string, bookingId?: number): Observable<any> {
    return this.http
      .patch<any>(`${this.API_URL}/${paymentId}/status`, {
        statusCode: status.toUpperCase(),
        bookingId: bookingId ? bookingId.toString() : null,
      }).pipe(map((response) => response.data));
  }
  
  createInvoice(request: any, productId: string): Observable<any> {
    const headers = new HttpHeaders({ 'X-Product-Id': productId });
    return this.http
      .post<any>(`${this.apiUrl}/invoices/create-invoice`, request, { headers })
      .pipe(map((response) => response.data));
  }

  validatePaymentToken(paymentToken: string): Observable<any> {
    const params = new HttpParams().set('paymentToken', paymentToken);

    return this.http
      .get<any>(`${this.apiUrl}/payment-tokens/validate?`, {params})
      .pipe(map((response) => response.data));
  }

  setTokenData(data: any): void {
    this.tokenDataSubject.next(data);
  }

  getTokenData(): Observable<any> {
    return this.tokenDataSubject.asObservable();
  }

  getTokenDataSnapshot(): any {
    return this.tokenDataSubject.value;
  }
}
